import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Save } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useProjects } from '../../contexts/ProjectsContext';
import { useReports } from '../../contexts/ReportsContext';
import { apiGetManagers } from '../../utils/api';
import { calculateOvertimeHours, calculateTotalHours } from '../../utils/dateHelpers';

const fullDayWorkTypes = ['Выезд в выходной день', 'Работа в выходной день', 'Возврат в выходной день'];
const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutes = ['00', '15', '30', '45'];

export const OvertimeEditForm = ({ report, onClose }) => {
  const { darkMode } = useTheme();
  const { projects, loadingProjects } = useProjects();
  const { editOvertimeReport } = useReports();

  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);

  const [formData, setFormData] = useState({
    date: report.date || new Date().toISOString().split('T')[0],
    startTime: report.startTime || '09:00',
    endTime: report.endTime || '18:00',
    project: report.project || '',
    manager: report.manager || '',
    workType: report.workType || 'Переработка',
  });

  const [submitting, setSubmitting] = useState(false);

  // Состояния для автодополнения проектов
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectInputRef = useRef(null);

  // Состояния для автодополнения руководителей
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const managerInputRef = useRef(null);

  useEffect(() => {
    apiGetManagers()
      .then(data => {
        if (data.success) setManagers(data.managers || []);
        setLoadingManagers(false);
      })
      .catch(() => setLoadingManagers(false));
  }, []);

  // Фильтрация проектов при вводе текста
  useEffect(() => {
    if (showProjectDropdown) {
      const query = formData.project.trim();
      const filtered = query === '' ? projects : projects.filter(p => p.toLowerCase().includes(query.toLowerCase()));
      setFilteredProjects(filtered);
    }
  }, [formData.project, showProjectDropdown, projects]);

  // Фильтрация руководителей при вводе текста
  useEffect(() => {
    if (showManagerDropdown) {
      const query = (formData.manager || '').trim();
      const filtered = query === '' ? managers : managers.filter(m => m.toLowerCase().includes(query.toLowerCase()));
      setFilteredManagers(filtered);
    }
  }, [formData.manager, showManagerDropdown, managers]);

  // Закрытие списков при клике вне полей
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectInputRef.current && !projectInputRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
      if (managerInputRef.current && !managerInputRef.current.contains(event.target)) {
        setShowManagerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async () => {
    if (!formData.date || !formData.startTime || !formData.endTime || !formData.project.trim()) {
      alert('Заполните обязательные поля');
      return;
    }
    const totalHours = calculateTotalHours(formData.startTime, formData.endTime);
    const overtimeHours = fullDayWorkTypes.includes(formData.workType)
      ? totalHours
      : calculateOvertimeHours(formData.startTime, formData.endTime);

    setSubmitting(true);
    const updatedReport = {
      ...formData,
      totalHours,
      overtimeHours,
      month: new Date(formData.date).toLocaleString('ru-RU', { month: 'long', year: 'numeric' }),
    };
    const result = await editOvertimeReport(report.id, updatedReport);
    setSubmitting(false);

    if (result.success) {
      alert('Отчет успешно обновлён!');
      onClose();
    } else {
      alert(`Ошибка: ${result.error}`);
    }
  };

  if (loadingProjects || loadingManagers) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`p-6 rounded-lg flex items-center ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}>
          <Loader className="w-6 h-6 animate-spin text-blue-500 mr-2" />
          <span>Загрузка данных...</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center p-0 md:p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-h-[90vh] overflow-y-auto rounded-t-xl md:rounded-xl p-5 md:p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} md:max-w-md relative`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg md:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Редактировать отчёт
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Дата */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Дата</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
            />
          </div>

          {/* Время работы */}
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Начало работы</label>
              <div className="flex space-x-2">
                <select
                  value={formData.startTime.split(':')[0] || '09'}
                  onChange={(e) => setFormData({ ...formData, startTime: `${e.target.value}:${formData.startTime.split(':')[1] || '00'}` })}
                  className={`flex-1 px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
                >
                  {hours.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span className="self-center">:</span>
                <select
                  value={formData.startTime.split(':')[1] || '00'}
                  onChange={(e) => setFormData({ ...formData, startTime: `${formData.startTime.split(':')[0] || '09'}:${e.target.value}` })}
                  className={`flex-1 px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
                >
                  {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Окончание работы</label>
              <div className="flex space-x-2">
                <select
                  value={formData.endTime.split(':')[0] || '18'}
                  onChange={(e) => setFormData({ ...formData, endTime: `${e.target.value}:${formData.endTime.split(':')[1] || '00'}` })}
                  className={`flex-1 px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
                >
                  {hours.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span className="self-center">:</span>
                <select
                  value={formData.endTime.split(':')[1] || '00'}
                  onChange={(e) => setFormData({ ...formData, endTime: `${formData.endTime.split(':')[0] || '18'}:${e.target.value}` })}
                  className={`flex-1 px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
                >
                  {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Проект с автодополнением */}
          <div className="relative" ref={projectInputRef}>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Проект</label>
            <input
              type="text"
              value={formData.project}
              onClick={() => setShowProjectDropdown(true)}
              onChange={(e) => {
                setFormData({ ...formData, project: e.target.value });
              }}
              className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
              placeholder="Введите название проекта..."
              autoComplete="off"
            />
            {showProjectDropdown && filteredProjects.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-300'}`}>
                {filteredProjects.map((project, idx) => (
                  <div
                    key={idx}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setFormData({ ...formData, project });
                      setShowProjectDropdown(false);
                    }}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 ${formData.project === project ? 'bg-blue-50 dark:bg-blue-900/50 font-medium' : ''}`}
                  >
                    {project}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Руководитель проекта с автодополнением */}
          <div className="relative" ref={managerInputRef}>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Руководитель проекта</label>
            <input
              type="text"
              value={formData.manager || ''}
              onClick={() => setShowManagerDropdown(true)}
              onChange={(e) => {
                setFormData({ ...formData, manager: e.target.value });
              }}
              className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
              placeholder="Введите имя руководителя..."
              autoComplete="off"
            />
            {showManagerDropdown && filteredManagers.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-300'}`}>
                {filteredManagers.map((manager, idx) => (
                  <div
                    key={idx}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setFormData({ ...formData, manager });
                      setShowManagerDropdown(false);
                    }}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 ${formData.manager === manager ? 'bg-blue-50 dark:bg-blue-900/50 font-medium' : ''}`}
                  >
                    {manager}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Вид работы */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Вид работы</label>
            <select
              value={formData.workType}
              onChange={(e) => setFormData({ ...formData, workType: e.target.value })}
              className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'}`}
            >
              <option value="Переработка">Переработка</option>
              <option value="Переработка дорога">Переработка дорога</option>
              <option value="Выезд в выходной день">Выезд в выходной день</option>
              <option value="Работа в выходной день">Работа в выходной день</option>
              <option value="Возврат в выходной день">Возврат в выходной день</option>
            </select>
          </div>

          {fullDayWorkTypes.includes(formData.workType) && (
            <div className={`p-3 rounded-lg text-sm ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
              ℹ️ Для выбранного типа работы оплачивается весь день ({calculateTotalHours(formData.startTime, formData.endTime).toFixed(2)} ч.)
            </div>
          )}

          {/* Кнопки */}
          <div className="flex flex-col-reverse xs:flex-row justify-end space-y-reverse space-y-2 xs:space-y-0 xs:space-x-3 pt-4">
            <button onClick={onClose} className={`w-full xs:w-auto px-6 py-3 rounded-lg transition-colors touch-target ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>
              Отмена
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full xs:w-auto px-6 py-3 rounded-lg transition-colors flex items-center justify-center touch-target bg-blue-600 hover:bg-blue-700 text-white ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {submitting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Сохранить изменения
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};